import React, {useEffect, useState} from "react";
import {Button, Form, Icon, Input, Label, Message, Select,} from "semantic-ui-react";
import {Controller, useForm, useWatch} from "react-hook-form";
import Flatpickr from "react-flatpickr";
import TimePicker from "rc-time-picker";
import {yupResolver} from "@hookform/resolvers/yup";
import moment, {Moment} from "moment";
import {useAppDispatch, useAppSelector} from "../../../hooks/hooks";
import { createDartsGame, editDartsGame, hideGamesForm } from "./DartsGamesCRUD.slice";
import { ICreateDartsGamePayload, IEditDartsGamePayload } from "./DartsGamesCRUD.interface";
import { IDartsTournament } from "../../Tournaments/TournamentsCRUD.types";
import useGetOptions from "./hooks/useGetOptions";
import {createValidationSchema} from "./helpers/getValidationSchema";
import {createTitleForDatePicker} from "./helpers/getTitleForDatePicker";
import CheckboxCms from "../../../components/CheckboxCms/CheckboxCms";

const DartsGamesCRUDForm = () => {
    const [dateStart, setDateStart] = useState<Date | string>(new Date());
    const [timeValue, setTimeValue] = useState<Moment>(moment());
    const [doubleStart, setDoubleStart] = useState(false);
    const [startScore, setStartScore] = useState<any>(501);
    const [selectedTour, setSelectedTour] = useState<IDartsTournament | null>(null)
    const dispatch = useAppDispatch();
    const { editMode, dartsGames, editGameId } = useAppSelector(state => state.DartsGamesCRUD);
    const {countries, tournaments, players, groups, types} = useAppSelector(state => state.DartsGamesCRUD.initGamesData);
    const {
        countriesOptions,
        playersOptions,
        tournamentsOptions,
        groupOptions,
        typesOptions
    } = useGetOptions({countries, tournaments, players, groups, types})

    const startScoreOptions = [
        {key: 301, value: 301, text: "301"},
        {key: 501, value: 501, text: "501"},
        {key: 701, value: 701, text: "701"}
    ]

    useEffect(() => {
        setValue("location", "GB");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setDefaultFormValues()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editGameId, editGameId]);

    const setDefaultFormValues = () => {
        if (editMode && editGameId) {
            const editGame = dartsGames?.find((game) => game.id === editGameId);
            const defaultTournamentsOptions = tournamentsOptions
                .find((tour) => tour.text === editGame!.tournament);
            const defaultCountryOptions = countriesOptions?.find((country) => country.text === editGame!.location);
            const defaultPlayer1 = playersOptions?.find((player) => player.text === editGame!.players.split(", ")[0]);
            const defaultPlayer2 = playersOptions?.find((player) => player.text === editGame!.players.split(", ")[1]);
            if (editGame) {
                setValue("legsToWin", editGame.legsToWin)
                setDoubleStart(editGame.doubleStart)
            }
            setValue("tournamentName", defaultTournamentsOptions?.value);
            setValue("location", defaultCountryOptions?.key);
            setValue("player1", defaultPlayer1?.value);
            setValue("player2", defaultPlayer2?.value);

            setDateStart(editGame!.timeStart);
            setTimeValue(moment(editGame!.timeStart));
            if (defaultTournamentsOptions) {
                const currentTour = tournaments?.find(tour => tour.id === defaultTournamentsOptions.value)
                if (currentTour) {
                    setSelectedTour(currentTour)
                }
            }


        }
    }

    const gamesValidationSchema = createValidationSchema();

    const { control, formState: { errors }, handleSubmit, setValue, getValues} = useForm({
        mode: "onChange",
        resolver: yupResolver(gamesValidationSchema),
    });
    const formatTimeStart = () => {
        const localDateTime = moment(`${moment(dateStart).format("YYYY-MM-DD")}T${timeValue.format("HH:mm:ss")}`);
        return localDateTime.utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
    }

    const handleSubmitGame = () => {
        const game: ICreateDartsGamePayload = {
            tournamentId: getValues("tournamentName"),
            playerId1: getValues("player1"),
            playerId2: getValues("player2"),
            playerId3: getValues("player3"),
            status: 1,
            location: getValues("location"),
            timeFixture: formatTimeStart(),
            legsToWin: getValues("legsToWin") ? Number(getValues("legsToWin")) : 4,
            doubleStart,
            startScore,
            group: getValues("group"),
            type:  getValues("type"),
            legsToWin: getValues("legsToWin") ? Number(getValues("legsToWin")) : 4
        };
        dispatch(createDartsGame(game));
    };

    const submitEditGame = () => {
        const editGame: IEditDartsGamePayload = {
            id: editGameId!,
            tournamentId: getValues("tournamentName"),
            playerId1: getValues("player1"),
            playerId2: getValues("player2"),
            status: 1,
            location: getValues("location"),
            timeFixture: formatTimeStart(),
            legsToWin: getValues("legsToWin"),
            doubleStart,
            startScore,
            group: getValues("group"),
            type: getValues("type").value,
        };
        dispatch(editDartsGame(editGame));
    };

    const changeTimeStart = (value: Moment) => {
        setTimeValue(value);
    };

    const dayPickerOptions = {
        dateFormat: "Y-m-d",
        locale: {
            firstDayOfWeek: 1,
        },
    };

    const onCloseForm = () => {
        dispatch(hideGamesForm())
    }

    const watchedType = useWatch({control, name: "type"});
    const watchedTournament = useWatch({control, name: "tournamentName"});

    return (
        <div className="form-overlay-general">
            <Form
                onSubmit={
                    editMode
                        ? handleSubmit(submitEditGame)
                        : handleSubmit(handleSubmitGame)
                }
                className={"darts-game-form"}
            >
                <Icon
                    color="red"
                    name="close"
                    size="large"
                    onClick={onCloseForm}
                    style={{float: "right", cursor: "pointer"}}
                />
                <div className={"dartsGame-form-row"}>
                    {!watchedType && watchedType !== 0 &&
                        <Form.Field className={"half-width-field"}>
                            <Label>Tournament:</Label>
                            <Controller name="tournamentName" control={control}
                                render={({ field }) => (
                                    <Select clearable id="tournamentName" options={tournamentsOptions} placeholder="Select tournament" { ...field} />
                                )}
                            />
                        </Form.Field>
                    }
                    {!watchedTournament &&
                        < Form.Field className={"half-width-field"}>
                            <Label>Type</Label>
                            <Controller name="type" control={control}
                                render={({ field }) => (
                                    <Select clearable id="type" options={typesOptions} placeholder="Select type" { ...field} />
                                )}
                            />
                        </Form.Field>
                    }
                    <Form.Field className={"half-width-field"}>
                        <Label>Group</Label>
                        <Controller name="group" control={control}
                            render={({ field }) => (
                                <Select id="GroupName" options={groupOptions} placeholder="Select group" { ...field} />
                            )}
                        />
                        {errors.group && (
                            <Message negative>{"Group is required field"}</Message>
                        )}
                    </Form.Field>
                </div>
                {errors.tournamentName && (
                    <Message negative>{errors.tournamentName.message}</Message>
                )}
                <div className={"dartsGame-form-row"}>
                    <Form.Field className={"half-width-field"}>
                        <Label>Player 1:</Label>
                        <Controller name="player1" control={control}
                            render={({ field }) => (
                                <Select id="player1" options={playersOptions} placeholder="Select player 1" { ...field} />
                            )}
                        />
                        {errors.player1 && (
                            <Message negative>{"Player1 is required field"}</Message>
                        )}
                    </Form.Field>
                    <Form.Field className={"half-width-field"}>
                        <Label>Player 2:</Label>
                        <Controller name="player2" control={control}
                            render={({ field }) => (
                                <Select id="player2" options={playersOptions} placeholder="Select player 2" error={!!errors.player2} { ...field} />
                            )}
                        />
                        {errors.player2 && (
                            <Message negative>{"Player2 is required field"}</Message>
                        )}
                        {errors.isValid && (
                            <Message negative>{errors.isValid.message}</Message>
                        )}
                    </Form.Field>
                    <Form.Field className={"half-width-field"}>
                        <Label>Player 3:</Label>
                        <Controller name="player3" control={control}
                            render={({ field }) => (
                                <Select id="player3" options={playersOptions} placeholder="3 player for shootout" {...field} />
                            )}
                        />
                        {errors.player3 && (
                            <Message negative>{"Player3 is required field"}</Message>
                        )}
                        {errors.isValid && (
                            <Message negative>{errors.isValid.message}</Message>
                        )}
                    </Form.Field>
                </div>
                <Form.Field className={"half-width-field"}>
                    <Label>Location:</Label>
                    <Controller name="location" control={control}
                        render={({ field }) => (
                            <Select options={countriesOptions} placeholder="Select country" {...field} />
                        )}
                    />
                    {errors.location && (
                        <Message negative>{"Location is required field"}</Message>
                    )}
                </Form.Field>
                <div className={"dartsGame-form-row"}>
                    <Form.Field className={"half-width-field"}>
                        <Label>{createTitleForDatePicker(selectedTour)}</Label>
                        <Flatpickr
                            name="date"
                            value={dateStart}
                            options={dayPickerOptions}
                            onChange={(date: Date, dayStr: string) => setDateStart(dayStr)}
                        />
                        {errors.isValidTournamentDate && (
                            <Message negative>{errors.isValidTournamentDate.message}</Message>
                        )}
                    </Form.Field>
                    <Form.Field className={"half-width-field"}>
                        <Label>Fixture Time:</Label>
                        <div>
                            <TimePicker
                                showSecond={false}
                                value={timeValue}
                                onChange={changeTimeStart}
                            />
                        </div>
                    </Form.Field>
                </div>
                <Form.Field className={"half-width-field"}>
                    <Label>Start score</Label>
                    <Controller name="startScore" control={control}
                        render={({ field }) => (
                            <Select id="playerAwayName" options={startScoreOptions} placeholder="Select start score"  {...field}
                                onChange={(e, {value}) => {
                                    setStartScore(value)
                                }}
                            />
                        )}
                    />
                </Form.Field>
                <div className={"dartsGame-form-legs"}>
                    <Form.Field className={"leg-to-win-field"}>
                        <Label className={'darts-game-adc-label'}>Legs to win (min: 2 max: 9)</Label>
                        <Controller name="legsToWin" control={control}
                            render={({ field }) => (
                                <Input defaultValue={4} min={2} max={9} placeholder="Select legs to win (number)" type='number' id="legsToWin"  {...field} />
                            )}
                        />
                        {errors.legsToWin && (
                            <Message negative>{errors.legsToWin.message}</Message>
                        )}
                    </Form.Field>
                    <div className={"double-start"}>Double Start
                        <CheckboxCms value={doubleStart} handler={() => setDoubleStart(!doubleStart)}/>
                    </div>
                </div>
                <div className={"create-game-submit"}>
                    <Button id="save_game" color="green" type="submit">
                        Submit
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default DartsGamesCRUDForm;
